import React from 'react';
import { View, StyleSheet, TouchableOpacity, Image } from 'react-native';
import { Text } from '@/components/common/text';
import { useMarketplaceColors } from '@/constants/residents/marketplaceColors';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import { getInitials } from '@/utils/residents/utils';
import { formatAutopayDate, formatAutopayAmount } from '@/utils/residents/date';

const TrashIcon = RESIDENTS_SVG_COMPONENTS.TRASH;
const UserIcon = RESIDENTS_SVG_COMPONENTS.PROFILE_PLACEHOLDER;

export interface AutopayInfo {
  id: number;
  amount?: number;
  isFullAmount?: boolean;
  startDate?: string;
  endDate?: string;
  sourceId?: number;
  paymentMethodLabel?: string;
}

export interface ThirdPartyAutopay {
  email: string;
  name?: string;
  autopay?: AutopayInfo;
}

interface AutopayDetailsContentProps {
  accountName: string;
  firstName?: string;
  lastName?: string;
  photoUrl?: string;
  autopay: AutopayInfo;
  thirdPartyAutopays?: ThirdPartyAutopay[];
  onEditSettings: () => void;
  onRemove: () => void;
}

/**
 * Autopay Details Content
 * Matches Figma design 8 (resident only) and 8.a (with 3rd party)
 */
export const AutopayDetailsContent: React.FC<AutopayDetailsContentProps> = ({
  accountName,
  firstName,
  lastName,
  photoUrl,
  autopay,
  thirdPartyAutopays = [],
  onEditSettings,
  onRemove,
}) => {
  const MARKETPLACE_COLORS = useMarketplaceColors();
  const initials = getInitials(firstName, lastName) || accountName.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2);

  const renderAutopayCard = (
    isOwnAccount: boolean,
    name: string,
    email?: string,
    autopayInfo?: AutopayInfo,
    showActions: boolean = false,
  ) => {
    return (
      <View style={styles.userCard}>
        {/* Avatar */}
        <View style={styles.avatarContainer}>
          {isOwnAccount && photoUrl ? (
            <Image source={{ uri: photoUrl }} style={styles.avatarImage} />
          ) : isOwnAccount ? (
            <Text scale="micro" style={[styles.avatarInitials, { color: MARKETPLACE_COLORS.textPrimary }]}>{initials}</Text>
          ) : (
            <UserIcon width={16} height={20} color={MARKETPLACE_COLORS.textPrimary} />
          )}
        </View>

        {/* Info Section */}
        <View style={styles.infoSection}>
          {/* Top - Name and Status */}
          <View style={styles.topSection}>
            <Text style={[styles.accountName, { color: MARKETPLACE_COLORS.textPrimary }]} numberOfLines={2}>{name}</Text>
            <Text style={[styles.statusText, { color: MARKETPLACE_COLORS.textPrimary }]}>
              {isOwnAccount 
                ? 'You have set up autopay settings' 
                : 'This account has set up autopay settings'}
            </Text>
          </View>

          {/* Bottom - Autopay Details */}
          {autopayInfo && (
            <View style={styles.detailsSection}>
              {/* Autopay amount */}
              <View style={styles.detailRow}>
                <Text style={[styles.detailLabel, { color: MARKETPLACE_COLORS.textPrimary }]}>Autopay amount</Text>
                <Text style={[styles.detailValue, { color: MARKETPLACE_COLORS.textSecondary }]}>
                  {formatAutopayAmount(autopayInfo.amount, autopayInfo.isFullAmount)}
                </Text>
              </View>

              {/* Autopay start */}
              <View style={styles.detailRow}>
                <Text style={[styles.detailLabel, { color: MARKETPLACE_COLORS.textPrimary }]}>Autopay start</Text>
                <Text style={[styles.detailValue, { color: MARKETPLACE_COLORS.textSecondary }]}>{formatAutopayDate(autopayInfo.startDate)}</Text>
              </View>

              {/* Autopay end */}
              <View style={styles.detailRow}>
                <Text style={[styles.detailLabel, { color: MARKETPLACE_COLORS.textPrimary }]}>Autopay end</Text>
                <Text style={[styles.detailValue, { color: MARKETPLACE_COLORS.textSecondary }]}>{formatAutopayDate(autopayInfo.endDate)}</Text>
              </View>

              {/* Processed */}
              <View style={styles.detailRow}>
                <Text style={[styles.detailLabel, { color: MARKETPLACE_COLORS.textPrimary }]}>Processed</Text>
                <Text style={[styles.detailValue, { color: MARKETPLACE_COLORS.textSecondary }]}>1st day of each month</Text>
              </View>

              {/* Payment method */}
              <View style={styles.detailRow}>
                <Text style={[styles.detailLabel, { color: MARKETPLACE_COLORS.textPrimary }]}>Payment method</Text>
                <Text style={[styles.detailValue, { color: MARKETPLACE_COLORS.textSecondary }]}>
                  {autopayInfo.paymentMethodLabel || '—'}
                </Text>
              </View>

              {/* Actions - only for own account */}
              {showActions && (
                <View style={styles.actionsRow}>
                  <TouchableOpacity 
                    style={styles.editButton}
                    onPress={onEditSettings}
                  >
                    <Text style={[styles.editButtonText, { color: MARKETPLACE_COLORS.accentBlue }]}>Edit settings</Text>
                  </TouchableOpacity>

                  <TouchableOpacity 
                    style={styles.removeButton}
                    onPress={onRemove}
                  >
                    <TrashIcon width={20} height={20} color={MARKETPLACE_COLORS.textMuted} />
                    <Text style={[styles.removeButtonText, { color: MARKETPLACE_COLORS.textMuted }]}>Remove</Text>
                  </TouchableOpacity>
                </View>
              )}
            </View>
          )}
        </View>
      </View>
    );
  };

  return (
    <View style={styles.container}>
      {/* Own Account Autopay */}
      {renderAutopayCard(
        true,
        accountName,
        undefined,
        autopay,
        true,
      )}

      {/* Third Party Autopays */}
      {thirdPartyAutopays.length > 0 && (
        <>
          <View style={[styles.divider, { backgroundColor: MARKETPLACE_COLORS.dividerGray }]} />
          {thirdPartyAutopays.map((thirdParty, index) => (
            <View key={index}>
              {renderAutopayCard(
                false,
                thirdParty.name || thirdParty.email,
                thirdParty.email,
                thirdParty.autopay,
                false,
              )}
              {index < thirdPartyAutopays.length - 1 && (
                <View style={[styles.divider, { backgroundColor: MARKETPLACE_COLORS.dividerGray }]} />
              )}
            </View>
          ))}
        </>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  userCard: {
    flexDirection: 'row',
    gap: 18,
    padding: 24,
  },
  avatarContainer: {
    width: 68,
    height: 68,
    borderRadius: 34,
    backgroundColor: 'rgba(238, 241, 244, 0.1)',
    justifyContent: 'center',
    alignItems: 'center',
    overflow: 'hidden',
  },
  avatarImage: {
    width: '100%',
    height: '100%',
  },
  avatarInitials: {
    fontSize: 24,
    fontWeight: '700',
    lineHeight: 36,
  },
  infoSection: {
    flex: 1,
    gap: 8,
  },
  topSection: {
    gap: 2,
  },
  accountName: {
    fontSize: 18,
    fontWeight: '600',
    lineHeight: 27,
  },
  statusText: {
    fontSize: 14,
    fontWeight: '400',
    lineHeight: 21,
  },
  detailsSection: {
    gap: 8,
  },
  detailRow: {
    gap: 4,
  },
  detailLabel: {
    fontSize: 12,
    fontWeight: '300',
    lineHeight: 18,
  },
  detailValue: {
    fontSize: 12,
    fontWeight: '300',
    lineHeight: 18,
  },
  actionsRow: {
    flexDirection: 'row',
    gap: 20,
    paddingTop: 6,
  },
  editButton: {
    paddingTop: 2,
  },
  editButtonText: {
    fontSize: 12,
    fontWeight: '500',
    lineHeight: 18,
  },
  removeButton: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    gap: 5,
  },
  removeButtonText: {
    fontSize: 12,
    fontWeight: '500',
    lineHeight: 18,
  },
  divider: {
    height: 1,
  },
});
